import { createColumnHelper } from '@tanstack/react-table';
import { getAllColumns, columnGroups } from '../config/tableColumns';
import DynamicColumnHeader from '../components/DynamicColumnHeader';
import SimpleColumnHeader from '../components/SimpleColumnHeader';
import PerformanceColumnHeader from '../components/ColumnHeaderWithControlPanel';
/**
 * Creates a table configuration with the specified columns
 * @param {string[]} columnIds - Array of column IDs to include in the table
 * @param {Object} options - Additional options for the table
 * @returns {Object} Table configuration object
 */
export const buildTable = (columnIds = [], options = {}, tableType) => {
  const columnHelper = createColumnHelper();
  const allColumns = getAllColumns();
  
  // Make sure output is always included at the end if not explicitly specified
  if (!columnIds.includes('output')) {
    columnIds.push('output');
  }
  
  // Build table columns based on provided IDs
  const tableColumns = columnIds.map(columnId => {
    const columnConfig = allColumns[columnId];
    
    if (!columnConfig) {
      console.error(`Column ${columnId} not found in column definitions`);
      return null;
    }
    
    if (tableType == "summary-table" && columnConfig.isDynamic) {
      // For dynamic P/F columns
      return columnHelper.accessor(columnId, {
        size: columnConfig.size || 200,
        header: ({ column, table }) => (
          <DynamicColumnHeader
            columnId={columnId}
            header={columnConfig.header}
            colorOption={table.options.state.columnConditions?.[columnId]?.colorOption}
            onConditionChange={(updatedConditions) => 
              table.options.updateColumnCondition?.(columnId, updatedConditions)
            }
            onColorOptionChange={(colorOption) => 
              table.options.updateColumnCondition?.(columnId, { colorOption })
            }
            isAllowedToChange={true} // This would come from props
            columnData={table.options.data.map(row => row[columnId])}
            isActive={table.options.state.activeColumns[columnId] || false}
            onToggleColumns={table.options.setColumnVisibility}
            onToggleColumnWidth={(columnId, isActive) => 
              table.options.toggleColumnWidth?.(columnId, isActive)
            }
            setColumnVisibility={table.options.setColumnVisibility}
            fontClass={columnConfig.fontClass}
          />
        ),
        cell: info => columnConfig.cell(info.getValue(), info),
      });
    }
    else if (columnConfig.isDynamic && tableType == 'analytics-table'){
      return columnHelper.accessor(columnId, {
        size: columnConfig.size || 200,
        header: ({ column, table }) => (
          <PerformanceColumnHeader 
            columnId={columnId}
            header={columnConfig.header}
            isAllowedToChange={true} // This would come from props
            columnData={table.options.data.map(row => row[columnId])}
            isActive={table.options.state.activeColumns[columnId] || false}
            onToggleColumns={table.options.setColumnVisibility}
            onToggleColumnWidth={(columnId, isActive) => 
              table.options.toggleColumnWidth?.(columnId, isActive)
            }
            setColumnVisibility={table.options.setColumnVisibility}
            fontClass={columnConfig.fontClass}
          />
        ),
        cell: info => columnConfig.cell(info.getValue(), info),
      });
    }
     else if (columnId === 'output') {
      // Special case for output column
      return columnHelper.display({
        id: 'output',
        header: ({ column, table }) => columnConfig.header({
          upperValue: table.options.upperValue,
          lowerValue: table.options.lowerValue,
          setUpperValue: table.options.setUpperValue,
          setLowerValue: table.options.setLowerValue
        }),
        cell: info => columnConfig.cell({
          row: info.row,
          column: info.column,
          columnConditions: info.table.options.state.columnConditions,
          upperValue: info.table.options.upperValue,
          lowerValue: info.table.options.lowerValue
        }),
      });
    } else if (columnId === 'actions') {
      // Special case for actions column
      return columnHelper.display({
        id: 'actions',
        header: () => '',
        cell: info => columnConfig.cell({
          row: info.row,
          startEditingRow: info.table.options.startEditingRow
        }),
        size: columnConfig.size || 30,
        minSize: columnConfig.minSize || 30,
        enableResizing: false,
      });
    } else if (columnId === 'name') {
      // Special case for name column (derived from first_name and last_name)
      return columnHelper.accessor(columnConfig.accessor, {
        id: 'name',
        header: ({ column, table }) => (
          <SimpleColumnHeader
            title={columnConfig.header}
            columnId={columnId}
            isPinned={table.options.state.pinnedColumns.includes(columnId)}
            onTogglePin={() => table.options.togglePinnedColumn?.(columnId)}
            isActive={table.options.state.activeColumns[columnId] || false}
            onToggleWidth={(id, active) => table.options.toggleColumnWidth?.(id, active)}
            fontClass={columnConfig.fontClass}
          />
        ),
        cell: info => columnConfig.cell(info.getValue(), info),
      });
    } else {
      // Standard column
      return columnHelper.accessor(columnId, {
        header: ({ column, table }) => (
          <SimpleColumnHeader
            title={columnConfig.header}
            columnId={columnId}
            isPinned={table.options.state.pinnedColumns.includes(columnId)}
            onTogglePin={() => table.options.togglePinnedColumn?.(columnId)}
            isActive={table.options.state.activeColumns[columnId] || false}
            onToggleWidth={(id, active) => table.options.toggleColumnWidth?.(id, active)}
            fontClass={columnConfig.fontClass}
          />
        ),
        cell: info => columnConfig.cell(info.getValue(), { 
          column: info.column, 
          dataEncrypted: info.table.options.state.dataEncrypted 
        }),
      });
    }
  }).filter(Boolean); // Remove any nulls from missing column configs
  
  return {
    columns: tableColumns,
    initialState: {
      columnVisibility: options.columnVisibility || {},
    },
  };
};

/**
 * Builds a table based on a predefined column group
 * @param {string} groupName - Name of the column group to use
 * @param {Object} options - Additional options for the table
 * @returns {Object} Table configuration object
 */
export const buildTableFromGroup = (groupName, options = {}) => {
  if (!columnGroups[groupName]) {
    console.error(`Column group ${groupName} not found`);
    return buildTable(columnGroups.basic, options);
  }
  
  return buildTable(columnGroups[groupName], options);
};

/**
 * Creates a custom table with the specified base group plus additional columns
 * @param {string} baseGroupName - Name of the base column group
 * @param {string[]} additionalColumns - Additional column IDs to include
 * @param {Object} options - Additional options for the table
 * @returns {Object} Table configuration object
 */
export const customizeTable = (baseGroupName, additionalColumns = [], options = {}) => {
  const baseGroup = columnGroups[baseGroupName] || columnGroups.basic;
  const combinedColumns = [...baseGroup];
  
  // Add additional columns, avoiding duplicates
  additionalColumns.forEach(colId => {
    if (!combinedColumns.includes(colId)) {
      combinedColumns.push(colId);
    }
  });
  
  return buildTable(combinedColumns, options);
};